import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Stack,
  Divider,
  useTheme,
  SelectChangeEvent,
  FormControl,
  Select,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import dayjs from '@/config/dayjs';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import StyledMenuItem from '@/components/common/styledMenuItem';
import { useTabs, Tab } from '@/hooks/useTabs';
import {
  defaultTransactionPeriod,
  type TransactionPeriod,
} from './transactionPeriod';
import { API_DATE_FORMAT, fromApiDate, parseAppDate } from '@/utils/date';
import FlyoutSectionHeader from '../common/FlyoutSectionHeader';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AccountBase } from '@/models/AccountViewModel';
import AccountsHistoricalGrid from '@/components/grids/historical/AccountsHistoricalGrid';
import HistoricalGrid from '@/components/grids/historical/HistoricalGrid';
import { mobileWidth } from '@/constants';
import DateRange from '@/components/date/DateRange';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

interface AccountTabsProps {
  /** The flyout's account; until it has loaded the tabs render nothing. */
  account: AccountBase | undefined;
  /** Which tab settings to load; defaults to investor-account */
  tabsPanelKey?: string;
  /** Optional: when provided, this tab becomes active on mount */
  initialTabId?: string;
  /** Optional: investor scope for advisor-context tab queries */
  investorId?: number;
}

const AccountTabs: React.FC<AccountTabsProps> = ({
  account,
  tabsPanelKey,
  initialTabId,
  investorId,
}) => {
  const [transactionPeriod, setTransactionPeriod] = useState<TransactionPeriod>(
    defaultTransactionPeriod,
  );
  const { tabs, defaultTab } = useTabs(
    account,
    tabsPanelKey,
    investorId,
    transactionPeriod,
  );
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobileMatch = useMediaQuery(mobileWidth);
  const [groupBy, setGroupBy] = useState('Today');

  const defaultDateRange = useMemo(() => {
    const range = getDateRangeFromFilter(DataRangeFilterEnum.OpenYear);
    return {
      startDate: dayjs(range.startDate).format('MM/DD/YYYY'),
      endDate: dayjs(range.endDate).format('MM/DD/YYYY'),
    };
  }, []);

  const [historicalStartDate, setHistoricalStartDate] = useState(
    defaultDateRange.startDate,
  );
  const [historicalEndDate, setHistoricalEndDate] = useState(
    defaultDateRange.endDate,
  );

  const handleHistoricalDateRangeChange = useCallback(
    (startDate: string, endDate: string) => {
      setHistoricalStartDate(startDate);
      setHistoricalEndDate(endDate);
    },
    [],
  );

  /**
   * Takes a picked range for the transactions tab. A half-picked or invalid
   * range -- the start chosen and the end not yet, a typed date that does not
   * parse, or an end before its start -- is ignored and the shown range stays.
   */
  const handleTransactionDateRangeChange = useCallback(
    (startDate: string, endDate: string) => {
      const start = parseAppDate(startDate);
      const end = parseAppDate(endDate);
      if (!start || !end || end.isBefore(start, 'day')) return;
      setTransactionPeriod({
        startDate: start.format(API_DATE_FORMAT),
        endDate: end.format(API_DATE_FORMAT),
      });
    },
    [],
  );

  const startingTabId =
    tabs.find((tab) => tab.id === initialTabId)?.id ?? defaultTab?.id;

  /**
   * The active tab is held by id and read from the current tabs, so a tab
   * whose content changes -- the transactions tab when its range is picked --
   * stays selected and shows its new content. Holding the tab object reset the
   * selection whenever the tabs were rebuilt. It moves to the starting tab only
   * when that changes, as when another account is opened.
   */
  const [activeTabId, setActiveTabId] = useState(startingTabId);
  useEffect(() => {
    setActiveTabId(startingTabId);
  }, [startingTabId]);
  const activeTab: Tab | undefined = tabs.find((tab) => tab.id === activeTabId);

  const handleGroupByChange = (event: SelectChangeEvent) => {
    setGroupBy(event.target.value);
  };

  const activeTabContent = useMemo(() => {
    if (!activeTab || !account) return null;

    if (activeTab.id === 'historical') {
      const historicalGridProps = {
        accountId: account.AccountId,
        liveBalance: account.Balance,
        startDate: dayjs(historicalStartDate, 'MM/DD/YYYY').format(
          'MM/DD/YYYY',
        ),
        endDate: dayjs(historicalEndDate, 'MM/DD/YYYY').format('MM/DD/YYYY'),
        dateFilter: DataRangeFilterEnum.OpenYear,
        size: 'flyout' as const,
      };

      return isMobileMatch ? (
        <AccountsHistoricalGrid {...historicalGridProps} />
      ) : (
        <HistoricalGrid {...historicalGridProps} />
      );
    }

    return activeTab.component;
  }, [
    activeTab,
    historicalStartDate,
    historicalEndDate,
    account,
    isMobileMatch,
  ]);

  if (!account) return null;

  /**
   * What sits above the active tab: a date range for the tabs that read one
   * (Historical Values and the transactions tab), otherwise the section header.
   */
  const renderTabHeader = () => {
    if (activeTab?.id === 'historical') {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateRange
            startDate={historicalStartDate}
            endDate={historicalEndDate}
            onChange={handleHistoricalDateRangeChange}
            useMobilePrimaryIconButton
          />
        </LocalizationProvider>
      );
    }
    if (activeTab?.id === 'transactions') {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateRange
            startDate={fromApiDate(transactionPeriod.startDate)}
            endDate={fromApiDate(transactionPeriod.endDate)}
            onChange={handleTransactionDateRangeChange}
            useMobilePrimaryIconButton
          />
        </LocalizationProvider>
      );
    }
    return (
      <FlyoutSectionHeader
        title={activeTab?.id === 'holdings' ? activeTab.title : ''}
        showGroupBy={activeTab?.id === 'holdings'}
        groupBy={groupBy}
        onGroupByChange={handleGroupByChange}
      />
    );
  };

  const handleTabSelectChange = (event: SelectChangeEvent) => {
    setActiveTabId(event.target.value);
  };

  return (
    <Stack
      direction='column'
      gap={3}
    >
      <Divider />

      {/* Tabs - Show as dropdown on mobile, inline tabs on desktop */}
      {isMobileMatch ? (
        <FormControl
          fullWidth
          size='small'
        >
          <Select
            labelId='account-tabs-select-label'
            id='account-tabs-select'
            value={activeTab?.id || ''}
            onChange={handleTabSelectChange}
            sx={(theme) => ({
              backgroundColor: theme.palette.custom.overlay.selected,
              color: theme.palette.primary.main,
              '& .MuiSelect-select': {
                color: theme.palette.primary.main,
                padding: '30px 14px',
                fontWeight: 600,
              },
              '& .MuiSelect-icon': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                display: 'none',
              },
              '& .MuiOutlinedInput-root': {
                border: 'none',
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none',
                },
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                display: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                display: 'none',
              },
            })}
          >
            {tabs.map((tab) => (
              <MenuItem
                key={tab.id}
                value={tab.id}
              >
                {tab.id === 'notes' ? (
                  <Stack
                    direction='row'
                    alignItems='center'
                    gap={0.5}
                  >
                    <span>{tab.title}</span>
                    {account.Memo && (
                      <CheckCircleIcon
                        color='success'
                        sx={{ fontSize: 16 }}
                      />
                    )}
                  </Stack>
                ) : (
                  tab.title
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Stack direction='row'>
          {tabs.map((tab) => (
            <StyledMenuItem
              key={tab.id}
              selected={activeTab?.id === tab.id}
              sx={{
                height: '40px',
                fontSize: '14px',
                fontWeight: 600,
                color: isDarkMode ? 'white' : 'rgba(0,0,0,60)',
              }}
              onClick={() => {
                setActiveTabId(tab.id);
              }}
            >
              {tab.id === 'notes' ? (
                <Stack
                  direction='row'
                  alignItems='center'
                  gap={0.5}
                >
                  <span>{tab.title}</span>
                  {account.Memo && (
                    <CheckCircleIcon
                      color='success'
                      sx={{ fontSize: 16 }}
                    />
                  )}
                </Stack>
              ) : (
                tab.title
              )}
            </StyledMenuItem>
          ))}
        </Stack>
      )}

      <Stack
        direction='column'
        gap={2}
      >
        {renderTabHeader()}

        {activeTabContent}
      </Stack>
    </Stack>
  );
};

export default React.memo(AccountTabs);
